import {useCallback, useEffect, useRef, useState} from 'react';
import ModalForm from 'components/ModalForm';
import {ModalFormFieldTypes} from 'components/formItems/FormGroup/FormGroup';
import Toast from 'components/Toast';
import SERVICES from 'services';
import {Button, Divider, Modal} from 'antd';

enum QuestionTypes {
	RADIO = 1,
	MULTY = 2,
	TEXT = 3
}

const generateSurveyFields = (questions: Question[], isRequired: boolean): ModalFormFieldTypes[] => {
	const fields: ModalFormFieldTypes[] = questions.map(q => {
		switch (q.surveyAnswerTypeID as QuestionTypes) {
			case QuestionTypes.MULTY:
				return {
					type: 'checkboxGroup',
					label: q.question,
					name: JSON.stringify({
						surveyQuestionID: q.id,
						valueKey: 'answerIDs'
					}),
					checkboxGroupProps: {
						options:
							q.answers?.map(ans => ({
								label: ans.answer,
								value: ans.id,
								commentIsRequired: ans.commentIsRequired
							})) || []
					},
					colProps: {
						xs: 24,
						lg: 24
					}
				};
			case QuestionTypes.RADIO:
				return {
					name: JSON.stringify({
						surveyQuestionID: q.id,
						valueKey: 'answerIDs'
					}),
					label: q.question,
					type: 'radio',
					radioProps: {
						radioFields: q.answers?.map(ans => ({
							label: ans.answer,
							value: ans.id,
							commentIsRequired: ans.commentIsRequired
						}))
					},
					colProps: {
						xs: 24,
						lg: 24
					}
				};
			case QuestionTypes.TEXT:
				return {
					type: 'textArea',
					name: JSON.stringify({
						surveyQuestionID: q.id,
						valueKey: 'answerText'
					}),
					label: q.question,
					textAreaProps: {
						rows: 4,
						className: 'ant-textarea bordered-textArea'
					},
					colProps: {
						xs: 24,
						lg: 24
					}
				};
		}
	});

	for (let idx = 0; idx < fields.length; idx++) {
		const i = fields[idx];

		if (i.type === 'radio' || i.type === 'checkboxGroup') {
			const options = i.checkboxGroupProps?.options || i.radioProps?.radioFields;
			const requiredIds = options
				?.filter((el: any) => el.commentIsRequired === true)
				.map((el: any) => el.value);

			fields.splice(idx + 1, 0, {
				type: 'textArea',
				name: JSON.stringify({...JSON.parse(i.name as string), valueKey: 'comment'}),
				label: 'Մեկնաբանություն',
				depFieldKey: i.name,
				depValue: () => true,
				isRequiredDep: i.name,
				isRequired: val => {
					if (Array.isArray(val)) {
						return requiredIds?.some((item: any) => val?.includes(item)) || false;
					}

					return requiredIds.includes(val);
				},
				textAreaProps: {
					rows: 4,
					className: 'ant-textarea bordered-textArea'
				},
				colProps: {
					xs: 24,
					lg: 24
				}
			});
		}
	}

	return fields;
};

interface IAnswer {
	id: number;
	answer: string;
	creationDate: string;
	status: number;
	studentRateCount: number;
	commentIsRequired: boolean;
}

interface Question {
	id: number;
	surveyTypeID: number;
	surveyID: number;
	question: string;
	surveyAnswerTypeID: number;
	surveyAnswerTypeName: string;
	creationDate: string;
	status: number;
	statusName: string;
	answers: IAnswer[];
}

export interface ISurveyData {
	id: number;
	title: string;
	academicYearID: number;
	surveyDistributeTypeID: number;
	academicYearName: string;
	semesterID: number;
	semesterName: string;
	lecturerID?: any;
	lecturerName?: string;
	surveyTypeID: number;
	surveyTypeName: string;
	startDate: string;
	endDate: string;
	isRequiredName: string;
	isRequired: boolean;
	creationDate: string;
	status: number;
	statusName: string;
	coursesNames: string;
	groupsNames: string;
	facultiesNames: string;
	flowsNames?: any;
	countOfStudentAnswers: number;
	questions: Question[];
}

const Surveys = () => {
	const [data, setData] = useState<ISurveyData[] | null>(null);
	const [currentData, setCurrentData] = useState<ISurveyData | null>(null);
	const [infoModal, setInfoModal] = useState(true);

	const counter = useRef(0);

	useEffect(() => {
		SERVICES.USER.getStudentSurveys().then(res => {
			if (res?.data?.data?.length) {
				setData(res.data.data);
			}
		});
	}, []);

	useEffect(() => {
		if (data?.length) {
			setCurrentData(data[counter.current]);
		}
	}, [data]);

	const handleCancel = (answered: boolean = false) => {
		if (!answered && currentData?.isRequired) {
			Toast.error('Այս հարցումը պարտադիր է։');
			return;
		}
		setCurrentData(null);
	};

	const handleNext = useCallback(
		(values: any) => {
			if (!currentData) return;

			const answers = Object.entries(values).map(i => {
				const {surveyQuestionID, valueKey} = JSON.parse(i[0]);
				const value = i[1];
				return {
					surveyQuestionID,
					[valueKey as 'answerText' | 'answerIDs' | 'comment']:
						typeof value === 'number' && !Array.isArray(value) ? [value] : value
				};
			});

			for (let i = 0; i < answers.length; i++) {
				const element = answers[i];
				if ('comment' in element) {
					const root = answers.find(
						ans => ans.surveyQuestionID === element.surveyQuestionID && !ans.comment
					);
					if (root) {
						root.comment = element.comment;
					}
					answers.splice(i, 1);
				}
			}

			SERVICES.USER.answerSurvey(currentData.id, answers).then(res => {
				Toast.success('Շնորհակալություն Ձեր պատասխանի համար');
				if (data?.length && data.length > ++counter.current) {
					setCurrentData(data[counter.current]);
				} else {
					handleCancel(true);
				}
			});
		},
		[currentData]
	);

	if (!currentData) return null;

	return (
		<>
			<Modal
				visible={infoModal}
				width={'80%'}
				bodyStyle={{
					width: '95%'
				}}
				onCancel={() => setInfoModal(false)}
				zIndex={9999999}
				footer={
					<Button size="middle" onClick={() => setInfoModal(false)} type="primary">
						Ok
					</Button>
				}>
				Հարգելի՛ ուսանող, <br /> Սույն հարցման նպատակը ՀՖՀՀ-ում դասավանդվող առարկաների և դասավանդման
				որակի կատարելագործմանը նպաստելն է: Հիշեցնենք, որ պատասխանները մշակվում են հարցման գաղտնիության
				և պատասխանների անանունության պահպանմամբ: Ուսանողի անունը չի կցվում պատասխանին, բացառությամբ
				այն դեպքերի, երբ ուսանողը մեկնաբանության դաշտում օգտագործել է խտրականության, ռասիստական,
				սեռական կամ այլ անընդունելի բովանդակություն պարունակող պատասխաններ: Նմանօրինակ դեպքում
				ուսումնական գծով տնօրենն իրավունք ունի բացառիկ կարգով համապատասխան ֆակուլտետի դեկանի և որակի
				հանձնաժողովի գրավոր կարծիքի հիման վրա վերացնել ուսանողի անանունությունը: Տեղեկացնում ենք, որ
				նշված ժամկետներում հարցմանը չմասնակցելու դեպքում համակարգը ավտոմատ կերպով կարգելափակի ձեր
				մուտքը ուսանողական բյուրո: <br />
				Մենք կարևորում ենք ձեր կարծիքը և ակտիվ մասնակցությունը:
				<Divider />
				Cher.e étudiant.e.
				<br /> Le but de cette enquête est de contribuer à l’amélioration de la qualité des cours et
				de l’enseignement à l’UFAR. Rappelons que vos réponses seront traitées de manière strictement
				anonyme et dans la plus grande confidentialité. Aucun nom d’étudiant ne sera lié aux réponses
				sauf les cas de mention discriminatoire, raciste, sexiste ou inacceptable en générale dans la
				partie qualitative du questionnaire. En pareil cas, la Direction des Études peut décider
				exceptionnellement avec l’avis écrit du doyen concerné et de la Commission Qualité de lever
				l’anonymat des réponses d’un étudiant. Informons que si vous ne remplissez pas le
				questionnaire dans le délai imparti, votre accès au bureau des étudiants sera automatiquement
				bloqué par le système. <br /> Vos avis et vos retours nous sont précieux.
			</Modal>
			<ModalForm
				title={currentData.title}
				visible={currentData ? 'ADD' : false}
				fields={generateSurveyFields(currentData.questions, currentData.isRequired)}
				onCancel={handleCancel}
				onCreate={handleNext}
				editModalType={'TAB'}
				dialogWidth={900}
				addModalType={'TAB'}
				formErrors={{}}
				loading={false}
			/>
		</>
	);
};

export default Surveys;
